import {ref} from 'vue';
import {hostApi} from '@/container';
import type {LiveOperator} from '@/live/interfaces';

/**
 * LIVE — the operator who took this counter, as Apodis Live last saw it at a
 * change of operator. Null until the first change since the watch started, on
 * shells that cannot report it, and where Apodis Live does not run.
 */
export const liveOperator = ref<LiveOperator | null>(null);

/** The operator as the API recorded it on the host, or null when it knows none. */
interface RecordedOperator {
    code: string;
    name: string | null;
}

/**
 * Take an operator change: shown in the notification, and written on this
 * workstation's host in the API, so the pharmacy knows who is at which counter.
 * The API answers with the operator it recorded, shown in place of what the
 * counter read: PharmaVitale only reads an internal number (« 10 »), which the
 * API ties to the operator the team knows (« 1 SOLENE »). An API that does not
 * answer it leaves the operator as read.
 */
export function recordLiveOperator(operator: LiveOperator, hostName: string | null): void {
    liveOperator.value = operator;

    if (!hostName) {
        return;
    }

    void hostApi()
        .updateOperator({
            name: hostName,
            operator_code: operator.code,
            operator_name: operator.name,
            changed_at: new Date(operator.timestamp).toISOString(),
        })
        .then((host: {operator?: RecordedOperator | null} | null) => {
            // Not over a newer change taken meanwhile.
            if (host?.operator && liveOperator.value === operator) {
                liveOperator.value = {...operator, code: host.operator.code, name: host.operator.name ?? ''};
            }
        });
}

/**
 * « 2 · Agnes »: the LGO writes names in upper case. A name that already
 * starts with the code (PharmaVitale: « 1 SOLENE ») is shown alone.
 */
export function liveOperatorShortLabel(operator: LiveOperator): string {
    const name = operator.name
        .toLowerCase()
        .replace(/(^|[\s-])(\p{L})/gu, (_, separator: string, letter: string) => separator + letter.toUpperCase());

    if (!name) {
        return operator.code;
    }

    return name.toUpperCase().startsWith(operator.code.toUpperCase()) ? name : `${operator.code} · ${name}`;
}

/** « Opérateur 2 · Agnes ». */
export function liveOperatorLabel(operator: LiveOperator): string {
    return `Opérateur ${liveOperatorShortLabel(operator)}`;
}
